"use client";
import { cn } from "@/lib/utils";
import { memo, useEffect, useRef, useState } from "react";

// Maximum updates per second before digit is hidden
const MAX_UPDATES_PER_SECOND = 4;

export function RealtimeCounter({
    initialTime,
    initialAmount,
    speed,
    currency,
}: {
    initialTime: number | null;
    initialAmount: number;
    // Speed is in currency units per second (e.g. 0.1 means 0.1 USD/second)
    speed: number | null;
    currency: string;
}) {
    const [digitsNew, setDigitsNew] = useState<
        { value: string; speed: number; type: "minus" | "dollar" | "decimal" | "cent" | "currency" }[]
    >(calculateDigits({ initialTime, initialAmount, speed, currency }));

    useEffect(() => {
        if (speed == null || speed === 0) return;

        const interval = setInterval(() => {
            setDigitsNew(calculateDigits({ initialTime, initialAmount, speed, currency }));
        }, 1000 / 15);

        return () => clearInterval(interval);
    }, [initialTime, initialAmount, speed, currency]);

    return (
        <div className="flex items-center font-mono tabular-nums tracking-tight">
            <div className="inline-flex">
                {digitsNew.map((digit, index) => {
                    if (digit.speed > MAX_UPDATES_PER_SECOND) {
                        return null;
                    }

                    if (digit.type === "currency" || digit.type === "minus") {
                        return (
                            <span key={index} className="inline-block w-[1ch]">
                                {digit.value}
                            </span>
                        );
                    }

                    if (digit.type === "decimal") {
                        return (
                            <span key={index} className="inline-block">
                                {digit.value}
                            </span>
                        );
                    }

                    return <AnimatedDigit digit={Number(digit.value)} key={index.toString()} speed={digit.speed} />;
                })}
            </div>
        </div>
    );
}

const calculateDigits = ({
    initialTime,
    initialAmount,
    speed,
    currency,
}: { initialTime: number | null; initialAmount: number; speed: number | null; currency: string }) => {
    const now = Date.now();
    const elapsedSeconds = (now - (initialTime ?? 0)) / 1000;
    const amount = initialAmount + (speed ?? 0) * elapsedSeconds;

    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    const formattedAmount = speed == null || speed === 0 ? absAmount.toFixed(2) : absAmount.toFixed(6);
    const [dollars, cents] = formattedAmount.split(".");
    const result: Array<{
        value: string;
        speed: number;
        type: "minus" | "dollar" | "decimal" | "cent" | "currency";
    }> = [];

    // Add minus sign if negative
    if (isNegative) {
        result.push({
            value: "-",
            speed: 0,
            type: "minus",
        });
    }

    const currencySymbol =
        new Intl.NumberFormat("en", {
            style: "currency",
            currency: currency,
            currencyDisplay: "narrowSymbol",
        })
            .formatToParts(0)
            .find((part) => part.type === "currency")?.value ?? currency;

    // add currency symbol
    result.push({
        value: currencySymbol,
        speed: 0,
        type: "currency",
    });

    // Process dollar digits
    dollars?.split("").forEach((digit, i) => {
        const positionValue = 10 ** (dollars.length - 1 - i);
        const digitSpeed = (speed ?? 0) / positionValue;
        result.push({
            value: digit,
            speed: Math.abs(digitSpeed),
            type: "dollar",
        });
    });

    // Add decimal point
    result.push({
        value: ".",
        speed: 0,
        type: "decimal",
    });

    // Process cent digits
    cents?.split("").forEach((digit, i) => {
        const positionValue = 10 ** (-1 - i);
        const digitSpeed = (speed ?? 0) / positionValue;
        result.push({
            value: digit,
            speed: Math.abs(digitSpeed),
            type: "cent",
        });
    });

    return result;
};

const AnimatedDigit = memo(function AnimatedDigit({ digit, speed }: { digit: number; speed: number }) {
    const digitRow1 = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    const digitRow2 = ["5", "6", "7", "8", "9", "0", "1", "2", "3", "4"];

    const digitOffset = (digit + 5) % 10;

    const opacity1 = Math.max(0, Math.min(digit / 4, (9 - digit) / 4));
    const opacity2 = Math.max(0, Math.min(digitOffset / 4, (9 - digitOffset) / 4));

    const duration = Math.min(1000 / speed, 1000);

    const textRef = useRef<HTMLSpanElement>(null);
    const [textHeight, setTextHeight] = useState(0);

    useEffect(() => {
        if (textRef.current) {
            setTextHeight(textRef.current.clientHeight);
        }
    }, []);

    return (
        <span className="relative w-[1ch] overflow-clip text-center text-foreground" ref={textRef}>
            <span
                className={cn(
                    "absolute inset-0 inline-flex flex-col mix-blend-darken transition-transform ease-in-out dark:mix-blend-plus-lighter",
                )}
                style={{
                    transform: `translateY(-${digit * textHeight}px)`,
                    opacity: opacity1,
                    transitionDuration: `${duration}ms`,
                }}
            >
                {digitRow1.map((d, index) => (
                    <span className="" key={index}>
                        {d}
                    </span>
                ))}
            </span>

            <span
                className={cn(
                    "absolute inset-0 inline-flex flex-col mix-blend-darken transition-transform ease-in-out dark:mix-blend-plus-lighter",
                )}
                style={{
                    transform: `translateY(-${digitOffset * textHeight}px)`,
                    opacity: opacity2,
                    transitionDuration: `${duration}ms`,
                }}
            >
                {digitRow2.map((d, index) => (
                    <span className="" key={index}>
                        {d}
                    </span>
                ))}
            </span>
        </span>
    );
});
